import { Navigate, Route, Routes, useOutletContext } from "react-router-dom";
import ClientVendorDirectoryListPage from "./ClientVendorDirectoryListPage";
import ClientVendorDirectoryCreatePage from "./ClientVendorDirectoryCreatePage";
import ClientVendorDirectoryWorkspacePage from "./ClientVendorDirectoryWorkspacePage";
import ClientVendorDirectoryImportListPage from "./ClientVendorDirectoryImportListPage";
import ClientVendorDirectoryImportPage from "./ClientVendorDirectoryImportPage";
import ClientVendorDirectoryCandidateListPage from "./ClientVendorDirectoryCandidateListPage";
import ClientVendorDirectoryCandidateDetailPage from "./ClientVendorDirectoryCandidateDetailPage";
import ClientVendorDirectoryDuplicateListPage from "./ClientVendorDirectoryDuplicateListPage";
import ClientVendorDirectoryDuplicateDetailPage from "./ClientVendorDirectoryDuplicateDetailPage";
import ClientVendorDirectoryResolutionListPage from "./ClientVendorDirectoryResolutionListPage";
import ClientVendorDirectoryResolutionDetailPage from "./ClientVendorDirectoryResolutionDetailPage";
import ClientVendorDirectorySourceListPage from "./ClientVendorDirectorySourceListPage";
import ClientVendorDirectoryCollectionPlanListPage from "./ClientVendorDirectoryCollectionPlanListPage";
import ClientVendorDirectoryCollectionRunListPage from "./ClientVendorDirectoryCollectionRunListPage";
import ClientVendorDirectoryVerificationListPage from "./ClientVendorDirectoryVerificationListPage";
import ClientVendorDirectoryVerificationDetailPage from "./ClientVendorDirectoryVerificationDetailPage";
import ClientVendorDirectoryCredentialListPage from "./ClientVendorDirectoryCredentialListPage";
import ClientVendorDirectoryCredentialDetailPage from "./ClientVendorDirectoryCredentialDetailPage";
import ClientVendorDirectoryPrivacyListPage from "./ClientVendorDirectoryPrivacyListPage";
import ClientVendorDirectoryPrivacyDetailPage from "./ClientVendorDirectoryPrivacyDetailPage";
import ClientVendorDirectoryClaimListPage from "./ClientVendorDirectoryClaimListPage";
import ClientVendorDirectoryClaimDetailPage from "./ClientVendorDirectoryClaimDetailPage";
import ClientVendorDirectoryRetentionDashboardPage from "./ClientVendorDirectoryRetentionDashboardPage";
import ClientVendorDirectoryRankingHubPage from "./ClientVendorDirectoryRankingHubPage";
import ClientVendorDirectoryAnalyticsHubPage from "./ClientVendorDirectoryAnalyticsHubPage";
import ClientVendorDirectoryMaintenanceHubPage from "./ClientVendorDirectoryMaintenanceHubPage";
import ClientVendorDirectoryPilotHubPage from "./ClientVendorDirectoryPilotHubPage";
import ClientVendorDirectoryExpansionHubPage from "./ClientVendorDirectoryExpansionHubPage";
import ClientVendorDirectoryMarketplaceHubPage from "./ClientVendorDirectoryMarketplaceHubPage";
import ClientVendorDirectorySettingsPage from "./ClientVendorDirectorySettingsPage";
import { useVendorDirectoryPermissions } from "../../hooks/useVendorDirectoryPermissions";
import { useLocale } from "../../intl";
import {
  VENDOR_DIRECTORY_PERMISSIONS,
  hasAnyVendorDirectoryPermission,
} from "../../auth/casamapaVendorDirectoryPermissions";

const HUB_PERMISSIONS = [
  VENDOR_DIRECTORY_PERMISSIONS.VENDORS_VIEW,
  VENDOR_DIRECTORY_PERMISSIONS.CANDIDATES_VIEW,
  VENDOR_DIRECTORY_PERMISSIONS.IMPORTS_VIEW,
  VENDOR_DIRECTORY_PERMISSIONS.DUPLICATES_VIEW,
  VENDOR_DIRECTORY_PERMISSIONS.RESOLUTION_VIEW,
  VENDOR_DIRECTORY_PERMISSIONS.SOURCES_VIEW,
  VENDOR_DIRECTORY_PERMISSIONS.COLLECTIONS_VIEW,
  VENDOR_DIRECTORY_PERMISSIONS.VERIFICATION_VIEW,
  VENDOR_DIRECTORY_PERMISSIONS.CREDENTIALS_VIEW,
  VENDOR_DIRECTORY_PERMISSIONS.PRIVACY_REQUESTS_VIEW,
  VENDOR_DIRECTORY_PERMISSIONS.CLAIMS_VIEW,
  VENDOR_DIRECTORY_PERMISSIONS.RANKING_VIEW,
  VENDOR_DIRECTORY_PERMISSIONS.ANALYTICS_VIEW,
  VENDOR_DIRECTORY_PERMISSIONS.MAINTENANCE_VIEW,
  VENDOR_DIRECTORY_PERMISSIONS.PILOT_VIEW,
  VENDOR_DIRECTORY_PERMISSIONS.EXPANSION_VIEW,
  VENDOR_DIRECTORY_PERMISSIONS.SETTINGS_VIEW,
];

export default function ClientVendorDirectoryHubPage() {
  const { activeView } = useOutletContext() || {};
  const { loading, permissions } = useVendorDirectoryPermissions();
  const { t } = useLocale();

  if (loading) {
    return (
      <div className="page-content">
        <p className="empty-state">{t("realEstate.casaMapa.admin.vendorDirectoryWorkspace.hub.loading")}</p>
      </div>
    );
  }

  if (!hasAnyVendorDirectoryPermission(permissions, HUB_PERMISSIONS)) {
    return (
      <div className="page-content">
        <h1>{t("realEstate.casaMapa.admin.vendorDirectoryWorkspace.hub.deniedTitle")}</h1>
        <p className="empty-state" role="alert">
          {t("realEstate.casaMapa.admin.vendorDirectoryWorkspace.hub.deniedBody")}
        </p>
      </div>
    );
  }

  const defaultRoute = permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.VENDORS_VIEW)
    ? "proveedores"
      : permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.CANDIDATES_VIEW)
        ? "candidatos"
        : permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.IMPORTS_VIEW)
          ? "importaciones"
          : permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.SOURCES_VIEW)
            ? "fuentes"
            : permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.VERIFICATION_VIEW)
              ? "verificacion"
              : permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.CREDENTIALS_VIEW)
                ? "credenciales"
                : permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.PRIVACY_REQUESTS_VIEW)
                  ? "privacidad"
                  : permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.CLAIMS_VIEW)
                    ? "reclamaciones"
                    : permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.RANKING_VIEW)
                      ? "posicionamiento"
                      : permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.ANALYTICS_VIEW)
                        ? "analytics"
                        : permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.MAINTENANCE_VIEW)
                          ? "mantenimiento"
                          : permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.PILOT_VIEW)
                          ? "piloto"
                          : permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.EXPANSION_VIEW)
                          ? "expansion"
                          : permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.MARKETPLACE_VIEW)
                          ? "marketplace"
                          : permissions.includes(VENDOR_DIRECTORY_PERMISSIONS.SETTINGS_VIEW)
                        ? "configuracion"
                        : "duplicados";

  return (
    <Routes>
      <Route index element={<Navigate to={defaultRoute} replace />} />
      <Route path="proveedores" element={<ClientVendorDirectoryListPage />} />
      <Route path="proveedores/nuevo" element={<ClientVendorDirectoryCreatePage />} />
      <Route path="proveedores/:vendorId" element={<ClientVendorDirectoryWorkspacePage />} />
      <Route path="importaciones" element={<ClientVendorDirectoryImportListPage />} />
      <Route path="importaciones/nuevo" element={<ClientVendorDirectoryImportPage isNew />} />
      <Route path="importaciones/:importJobId" element={<ClientVendorDirectoryImportPage />} />
      <Route path="candidatos" element={<ClientVendorDirectoryCandidateListPage />} />
      <Route path="candidatos/:candidateId" element={<ClientVendorDirectoryCandidateDetailPage />} />
      <Route path="duplicados" element={<ClientVendorDirectoryDuplicateListPage />} />
      <Route path="duplicados/:matchId" element={<ClientVendorDirectoryDuplicateDetailPage />} />
      <Route path="resolucion" element={<ClientVendorDirectoryResolutionListPage />} />
      <Route path="resolucion/:caseId" element={<ClientVendorDirectoryResolutionDetailPage />} />
      <Route path="fuentes" element={<ClientVendorDirectorySourceListPage />} />
      <Route path="colecciones/planes" element={<ClientVendorDirectoryCollectionPlanListPage />} />
      <Route path="colecciones/ejecuciones" element={<ClientVendorDirectoryCollectionRunListPage />} />
      <Route path="colecciones/ejecuciones/:runId" element={<ClientVendorDirectoryCollectionRunListPage />} />
      <Route path="verificacion" element={<ClientVendorDirectoryVerificationListPage />} />
      <Route path="verificacion/:caseId" element={<ClientVendorDirectoryVerificationDetailPage />} />
      <Route path="credenciales" element={<ClientVendorDirectoryCredentialListPage />} />
      <Route path="credenciales/:credentialId" element={<ClientVendorDirectoryCredentialDetailPage />} />
      <Route path="privacidad" element={<ClientVendorDirectoryPrivacyListPage />} />
      <Route path="privacidad/retencion" element={<ClientVendorDirectoryRetentionDashboardPage />} />
      <Route path="privacidad/:requestId" element={<ClientVendorDirectoryPrivacyDetailPage />} />
      <Route path="reclamaciones" element={<ClientVendorDirectoryClaimListPage />} />
      <Route path="reclamaciones/:claimId" element={<ClientVendorDirectoryClaimDetailPage />} />
      <Route path="posicionamiento/*" element={<ClientVendorDirectoryRankingHubPage />} />
      <Route path="analytics/*" element={<ClientVendorDirectoryAnalyticsHubPage />} />
      <Route path="mantenimiento/*" element={<ClientVendorDirectoryMaintenanceHubPage />} />
      <Route path="piloto/*" element={<ClientVendorDirectoryPilotHubPage />} />
      <Route path="expansion/*" element={<ClientVendorDirectoryExpansionHubPage />} />
      <Route path="marketplace/*" element={<ClientVendorDirectoryMarketplaceHubPage />} />
      <Route path="configuracion" element={<ClientVendorDirectorySettingsPage />} />
      <Route path="*" element={<Navigate to={defaultRoute} replace />} />
    </Routes>
  );
}
